import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.roman-storyworld.link";
const DEFAULT_IMAGE = `${SITE_URL}/og-preview.jpg`;

const ROUTE_META = {
  "/": {
    title: "Roman Storyworld | Interactive Roman Britain",
    description:
      "Explore Roman Britain through interactive maps, historical research, archaeology, timelines and the Sons of the Standard novels by Martin Antrobus.",
  },
  "/about": {
    title: "Martin Antrobus | Author of Roman Britain Historical Fiction",
    description:
      "Meet Martin Antrobus, author and creator of Roman Storyworld, where archaeology, landscape and historical fiction bring Roman Britain to life.",
  },
  "/books": {
    title: "Sons of the Standard Novels | Roman Britain Historical Fiction",
    description:
      "Discover Blood and Honour and Shadows of the Eagle, Martin Antrobus's historical novels of war, loyalty and survival in Roman Britain.",
  },
  "/characters": {
    title: "Characters of Sons of the Standard | Roman Storyworld",
    description:
      "Meet Varro, Aerwyna, Lucius and the Roman and tribal characters whose lives shape the Sons of the Standard novels.",
  },
  "/timeline": {
    title: "Roman Britain Timeline | Invasion, Rebellion and Empire",
    description:
      "Explore an illustrated timeline of Roman Britain from Caesar's expeditions through conquest, rebellion, frontier wars and the end of Roman rule.",
  },
  "/maps": {
    title: "Interactive Map of Roman Britain | Forts, Roads and Tribes",
    description:
      "Explore an interactive map of Roman Britain showing forts, roads, settlements, tribal territories, sacred places and archaeological discoveries.",
  },
  "/library": {
    title: "Roman Britain Research Library | Archaeology, Army and Tribes",
    description:
      "Read original research on Roman Britain's army, roads, settlements, tribal landscapes, religion, economy and archaeological remains.",
  },
  "/research": {
    title: "Roman Britain Research | Archaeology, History and Landscape",
    description:
      "Explore accessible historical and archaeological research into Roman Britain, its peoples, landscapes, military history and cultural change.",
  },
  "/military": {
    title: "Roman Military Archive | Legions, Campaigns and Fortresses",
    description:
      "Explore Roman legions, campaigns, frontier systems, fortresses, supply networks and military life in Britannia.",
  },
  "/media": {
    title: "Roman Storyworld Media | Images, Film and Historical Reconstructions",
    description:
      "Explore visual reconstructions, films and media inspired by the landscapes, archaeology and stories of Roman Britain.",
  },
  "/links": {
    title: "Roman Britain Podcasts and Resources | Roman Storyworld",
    description:
      "Listen to Roman Storyworld podcasts and discover selected resources exploring Roman Britain, archaeology and historical fiction.",
  },
  "/philosophy": {
    title: "History, Memory and Landscape | Roman Storyworld Philosophy",
    description:
      "Explore the ideas behind Roman Storyworld: walking the land, reading its traces and restoring human memory to Roman Britain's past.",
  },
  "/thesaurus": {
    title: "Roman Britain Visual Thesaurus | People, Places and Ideas",
    description:
      "Explore the connected people, places, themes and historical language of Roman Storyworld through an interactive visual thesaurus.",
  },
};

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

function upsertCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function upsertJsonLd(data) {
  const id = "roman-storyworld-page-schema";
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

export function Seo({
  title,
  description,
  path,
  type = "website",
  image = DEFAULT_IMAGE,
  jsonLd,
}) {
  useEffect(() => {
    const cleanPath = path === "/" ? "" : path.replace(/\/$/, "");
    const canonicalUrl = `${SITE_URL}${cleanPath}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index,follow" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertCanonical(canonicalUrl);

    upsertJsonLd(
      jsonLd || {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: canonicalUrl,
        inLanguage: "en-GB",
        isPartOf: {
          "@type": "WebSite",
          name: "Roman Storyworld",
          url: `${SITE_URL}/`,
        },
        author: {
          "@type": "Person",
          name: "Martin Antrobus",
        },
      }
    );
  }, [title, description, path, type, image, jsonLd]);

  return null;
}

export default function RouteSeo() {
  const { pathname } = useLocation();

  // These routes supply their own article-specific metadata.
  if (
    pathname.startsWith("/research/roman-britain-timeline/") ||
    pathname.startsWith("/library/") ||
    pathname.startsWith("/key-figures/") ||
    pathname.startsWith("/military/")
  ) {
    return null;
  }

  const meta = ROUTE_META[pathname] || ROUTE_META["/"];
  return <Seo {...meta} path={pathname} />;
}

